import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
	DeleteColumnOutlined,
	ExportOutlined,
	FileDoneOutlined,
	ImportOutlined,
	QuestionCircleOutlined,
	RedoOutlined,
	WarningFilled,
} from '@ant-design/icons';
import { blue, cyan, green, grey, gold, purple, red, lime, magenta, volcano, geekblue } from '@ant-design/colors';
import { Formatting } from '../../utils';
import styled from 'styled-components';
import moment from 'moment';
import { BDO, getReadableBdo, getReadablePaymentType, OrderType, TaskType } from 'types';
import { InternalTaskType, InternalTaskView } from 'reducers';

const getTaskColor = (task: InternalTaskView, noGrey?: boolean) => {
	if (!noGrey && task.doneAt) {
		return grey[3];
	}

	if (!noGrey && task.takenAt) {
		return blue[5];
	}

	if (task.orderType === OrderType.classic && task.order.onPlace) {
		return purple[5];
	}

	if (task.orderType === OrderType.loose && task.order.onPlace) {
		return magenta[3];
	}

	switch (task.internalType) {
		case InternalTaskType.classicDelivery:
			return green[5];

		case InternalTaskType.classicReplacement:
			return gold[5];

		case InternalTaskType.classicClaiming:
			return red[5];

		case InternalTaskType.looseDelivery:
			return cyan[4];

		case InternalTaskType.looseClaiming:
			return magenta[3];

		case InternalTaskType.own:
			return geekblue[5];

		case InternalTaskType.other:
			return lime[5];
	}

	return volcano[5];
};

const getTaskIcon = (task: InternalTaskView) => {
	switch (task.internalType) {
		case InternalTaskType.classicDelivery:
			return <ImportOutlined />;

		case InternalTaskType.classicReplacement:
			return <RedoOutlined />;

		case InternalTaskType.classicClaiming:
			return <ExportOutlined />;

		case InternalTaskType.own:
			return <FileDoneOutlined />;

		case InternalTaskType.looseDelivery:
			return <DeleteColumnOutlined style={{ transform: 'rotate(180deg)' }} />;

		case InternalTaskType.looseClaiming:
			return <DeleteColumnOutlined style={{ transform: 'rotate(180deg)' }} />;

		default:
			return <QuestionCircleOutlined />;
	}
};

const classicTaskType = (task: InternalTaskView) => {
	switch (task.type) {
		case TaskType.delivery:
			return '0';

		case TaskType.replacement:
			return '1';

		case TaskType.claiming:
			return '2';
	}
};

const Wrapper = styled.div<{ task: InternalTaskView }>`
	align-items: center;
	width: 100%;
	margin-bottom: 3px;
	padding: 10px 15px 10px 4px;
	border-radius: 5px;
	background-color: ${({ task }) => getTaskColor(task)};
	border-left: 24px solid ${({ task }) => getTaskColor(task, true)};

	&:last-child {
		margin-bottom: 0;
	}

	> p {
		color: white;
		font-size: 24px;
		margin-right: 8px;
	}

	> span {
		margin-right: 10px;

		svg {
			font-size: 35px;
			color: white;
		}
	}

	> div {
		flex: 1;

		p {
			color: white;
			white-space: nowrap;
			overflow: hidden;
			margin-bottom: 2px;
		}

		p:first-child {
			font-weight: 500;
			font-size: 20px;
			text-overflow: ellipsis;
		}

		p.phone {
			font-size: 20px;
		}

		p:last-child {
			font-weight: 400;
			font-size: 18px;
			margin-bottom: 0;
		}
	}
`;

const getBdoStatementState = (task: InternalTaskView) => {
	if (task.orderType !== OrderType.loose && task.orderType !== OrderType.classic) {
		return undefined;
	}

	if (task.order.bdoDocumentationComplete !== true) {
		return (
			<span>
				BDO niepotwierdzone <WarningFilled />
			</span>
		);
	}

	if (task.order.bdo === BDO.BDO) {
		return <span>Karta potwierdzona</span>;
	}

	return <span>Oświadczenie podpisane</span>;
};

interface Props {
	i: number;
	task: InternalTaskView;
}

export const TaskCell: React.FC<Props> = ({ i, task }) => {
	const navigate = useNavigate();

	const date = moment(task.date);

	if (task.orderType === OrderType.other) {
		return (
			<Wrapper className="flex" task={task} onClick={() => navigate(`/panel/orders/${task.id}/${task.orderType}`)}>
				<p>{i + 1}</p>
				{getTaskIcon(task)}
				<div>
					<p>{task.order.name}</p>
					<p>{date.hours() === 0 ? 'Cały dzień' : date.format('HH:mm')}</p>
				</div>
				<div>
					<p>
						{Formatting.money(task.order.value)} ({task.order.value_type === 0 ? 'netto' : 'brutto'})
					</p>
				</div>
			</Wrapper>
		);
	}

	if (task.orderType === OrderType.loose) {
		const paid = task.order.payments.reduce((acc, p) => acc + p.amount, 0);

		return (
			<Wrapper className="flex" task={task} onClick={() => navigate(`/panel/v2/tasks/${task.id}`)}>
				<p>{i + 1}</p>
				{getTaskIcon(task)}
				<div>
					<p>{task.order.address}</p>
					<p>
						{date.hours() === 0 ? 'Cały dzień' : date.format('HH:mm')} | {getReadableBdo(task.order.bdo)} |{' '}
						{getBdoStatementState(task)}
					</p>
					<p>
						{task.assignedUser?.name} {task.assignedUser?.surname}
					</p>
					<p>
						{getReadablePaymentType(task.order.wantedPaymentType)} |{' '}
						{paid >= task.order.grossPrice ? 'Opłacone' : 'Nieopłacone'}
					</p>
				</div>
				<div>
					<p>{task.client.name}</p>
					<p className="phone">{task.client.phone}</p>
					<p>Worków: {task.order.count}</p>
				</div>
			</Wrapper>
		);
	}

	if (task.orderType === OrderType.own) {
		return (
			<Wrapper className="flex" task={task} onClick={() => navigate(`/panel/orders/${task.id}/${task.orderType}`)}>
				<p>{i + 1}</p>
				{getTaskIcon(task)}
				<div>
					<p>{task.order.address}</p>
					<p>{date.hours() === 0 ? 'Cały dzień' : date.format('HH:mm')}</p>
				</div>
				<div>
					<p>
						{task.client.name} | {task.order.to}
					</p>
					{task.doneAt ? (
						<p>
							{Formatting.epochToDate(task.doneAt)} | {Formatting.insertSpaces(task.order.weight)} kg
						</p>
					) : (
						<p>{task.order.weight ? Formatting.insertSpaces(task.order.weight) : '-'} kg</p>
					)}
				</div>
			</Wrapper>
		);
	}

	return (
		<Wrapper className="flex" task={task} onClick={() => navigate(`/panel/tasks/${task.id}/${classicTaskType(task)}`)}>
			<p>{i + 1}</p>
			{getTaskIcon(task)}
			<div>
				<p>{task.order.address}</p>
				<p>
					{date.hours() === 0 ? 'Cały dzień' : date.format('HH:mm')} | {getReadableBdo(task.order.bdo)} |{' '}
					{getBdoStatementState(task)}
				</p>
				<p>
					{task.assignedUser?.name} {task.assignedUser?.surname}
				</p>
				<p>
					{getReadablePaymentType(task.order.paymentType)} |{' '}
					{task.order.paymentDate ? 'Opłacone' : task.order.advance ? 'Zaliczka' : 'Nieopłacone'}
				</p>
			</div>
			<div>
				<p>{task.client.name}</p>
				<p className="phone">{task.client.phone}</p>
				<p>{task.order.containerSize}</p>
				{/* <p>
					{Formatting.money(task.order.price)} ({getReadablePriceType(task.order.priceType)})
				</p> */}
			</div>
		</Wrapper>
	);
};
